import { FC, useState } from 'react';

import NumberFlow from '@number-flow/react';
import { ChevronDown, ExternalLink } from 'lucide-react';

import TokenImg from '@/components/TokenImg/TokenImg';
import { Button } from '@/components/shadcn/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/ui/collapsible';
import { Currency } from '@/constants';
import useDecomposeToken from '@/hooks/useDecomposeToken';
import { useWallet } from '@/hooks/useWallet';
import { NETWORK, useDogeWallet } from '@/networks/btc-like/doge';
import { useBtcLikeWalletUtxos } from '@/networks/btc-like/hooks';
import { calculatePrecisionNumberStats, cn, humanReadableStringFromTokenBigInt } from '@/utils';

import HeaderWalletDrawerBalanceCardTokenExplorerLink from '../HeaderWalletDrawerBalanceCardTokenExplorerLink';

export const HeaderWalletDrawerBalanceCardDoge: FC<{ shouldHideSmallBalances?: boolean }> = ({
  shouldHideSmallBalances,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dogeAccount = useDogeWallet();
  const wallet = useWallet(Currency.DOGE);
  const { token } = useDecomposeToken(Currency.DOGE);
  const { data: utxos } = useBtcLikeWalletUtxos(Currency.DOGE, dogeAccount.address);

  if (!wallet.isConnected) {
    return null;
  }
  const isHidden = Boolean(shouldHideSmallBalances) && Number(wallet.balanceUsd) < 0.1;
  if (isHidden) return null;

  const balanceConfirmedUtxos = !wallet.data.details.confirmed
    ? '0'
    : humanReadableStringFromTokenBigInt(wallet.data.details.confirmed, Currency.DOGE);

  const balanceUnconfirmedUtxos = !wallet.data.details.unconfirmed
    ? '0'
    : humanReadableStringFromTokenBigInt(wallet.data.details.unconfirmed, Currency.DOGE);

  const { address } = dogeAccount;

  const balanceToken = calculatePrecisionNumberStats(wallet.balanceToken, Currency.DOGE, 8).amountWithPrecision;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(
        'bg-background/60 flex w-full flex-col justify-between overflow-hidden rounded-xl',
        !isOpen && 'gap-0'
      )}
    >
      <CollapsibleTrigger
        asChild
        onClick={() => setIsOpen(!isOpen)}
        className={cn('flex cursor-pointer flex-col pb-0', {
          'bg-background hover:bg-background rounded-b-none pb-2': isOpen,
        })}
      >
        <div
          className="text-primary flex w-full flex-col items-center justify-center gap-0 overflow-hidden rounded-xl
            p-2.5 text-base sm:px-2.5 md:px-4"
        >
          <div className="flex w-full items-center gap-2">
            <div className="flex size-12 flex-col items-center justify-center">
              <TokenImg token={Currency.DOGE} className="size-8" />
            </div>
            <div className="flex w-full flex-col">
              <div className="flex flex-row items-center justify-between font-bold">
                <div className="flex items-center gap-1">
                  {token}
                  <HeaderWalletDrawerBalanceCardTokenExplorerLink currency={Currency.DOGE} />
                </div>
                <div>
                  <NumberFlow value={Number(balanceToken)} format={{ maximumFractionDigits: 8 }} />
                </div>
              </div>
              <div
                className="text-muted-foreground flex flex-row items-center justify-between text-[13px] leading-tight"
              >
                <div>Dogecoin</div>
                <div>
                  <NumberFlow value={Number(wallet.balanceUsd)} prefix="$ " format={{ minimumFractionDigits: 2 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent
        className="grid grid-cols-1 gap-0 rounded-b-xl *:mx-1 *:mt-1 *:w-[calc(100%-8px)] *:bg-transparent *:px-1.5
          *:md:p-3"
      >
        <Button
          asChild
          variant="secondary"
          className="font-roboto flex w-full cursor-pointer items-center justify-start gap-2 rounded-xl py-6 text-base"
        >
          <a
            target="_blank"
            rel="noreferrer noopener"
            href={NETWORK.MempoolApiEndpoint.concat(`/address/${address}/utxo`)}
          >
            <div className="flex w-12 justify-center">
              <ExternalLink className="size-4" />
            </div>
            <div className="flex w-full flex-row justify-between text-sm">
              <span>UTXOs confirmed ({utxos?.filter((utxo) => utxo.status.confirmed).length}):</span>
              <span>{balanceConfirmedUtxos}</span>
            </div>
          </a>
        </Button>

        <Button
          asChild
          variant="secondary"
          className="font-roboto flex w-full cursor-pointer items-center justify-start gap-2 rounded-xl py-6 text-base"
        >
          <a
            target="_blank"
            rel="noreferrer noopener"
            href={NETWORK.MempoolApiEndpoint.concat(`/address/${address}/utxo`)}
          >
            <div className="flex w-12 justify-center">
              <ExternalLink className="size-4" />
            </div>
            <div className="flex w-full flex-row justify-between text-sm">
              <span>UTXOs unconfirmed ({utxos?.filter((utxo) => !utxo.status.confirmed).length}):</span>
              <span>{balanceUnconfirmedUtxos}</span>
            </div>
          </a>
        </Button>
      </CollapsibleContent>

      <div
        className={cn('flex cursor-pointer justify-center pb-2', { 'pt-2': isOpen })}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <ChevronDown
          className={cn('text-muted-foreground size-4 transition-transform duration-200', {
            'rotate-180': isOpen,
          })}
        />
      </div>
    </Collapsible>
  );
};
